import { useTranslation } from "react-i18next";
import { Globe, ChevronRight, Check } from "lucide-react";
import { observer } from "mobx-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  changeLanguage,
  getCurrentLanguage,
  getSelectableLanguages,
  type SupportedLanguage,
} from "@/i18n";
import posthog from "@/utils/posthog";
import { showToastFromKey } from "@/lib/toast";
import { useRootStore } from "@/state/RootStore";
import * as Sentry from "@sentry/react";

const LANGUAGE_LABEL_KEY: Record<SupportedLanguage, string> = {
  en: "common.english",
  es: "common.spanish",
};

interface LanguageSelectorProps {
  variant?: "row" | "compact" | "settings";
}

export const LanguageSelector = observer(function LanguageSelector({
  variant = "row",
}: LanguageSelectorProps) {
  const { t, i18n } = useTranslation();
  const store = useRootStore();
  const current = (i18n.language || getCurrentLanguage()) as SupportedLanguage;
  const languages = getSelectableLanguages();
  const activeTest = store.testStore.activeTest;
  const hasActiveTest = !!activeTest && !activeTest.isFinished;

  const handleSelect = async (lang: SupportedLanguage) => {
    if (lang === current) return;
    // Mirror mobile: switching language mid-test would flip an in-progress
    // (or restorable) attempt to a different locale, so refuse and toast.
    if (hasActiveTest) {
      showToastFromKey({
        titleKey: "settings.language.title",
        descriptionKey: "settings.language.disabled_during_test",
      });
      return;
    }
    try {
      await changeLanguage(lang);
      posthog.capture("language_changed", { language: lang });
    } catch (error) {
      // Surface the failure rather than silently leaving the dropdown showing
      // the new selection while nothing actually changed.
      Sentry.captureException(error);
      showToastFromKey({
        titleKey: "errors.generic_title",
        descriptionKey: "errors.generic_body",
        variant: "destructive",
      });
    }
  };

  if (variant === "compact") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={t("settings.language.title")}
          className="inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-accent"
        >
          <Globe className="h-4 w-4" />
          <span>{t(LANGUAGE_LABEL_KEY[current])}</span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t("settings.language.title")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {languages.map((lang) => (
            <DropdownMenuItem
              key={lang}
              onSelect={() => void handleSelect(lang)}
              aria-current={lang === current ? "true" : undefined}
              className="flex items-center justify-between gap-3"
            >
              <span>{t(LANGUAGE_LABEL_KEY[lang])}</span>
              {lang === current && <Check className="h-4 w-4" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  const settingsVariant = variant === "settings";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t("settings.language.title")}
        className={settingsVariant
          ? "flex min-h-[72px] w-full items-center justify-between gap-4 px-6 text-left transition-colors hover:bg-[#fafafa] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--dp-brand-button)]"
          : "px-4 py-3 bg-white rounded-xl shadow-[0px_0px_8px_0px_rgba(0,0,0,0.08)] flex justify-between items-center w-full"
        }
      >
        <div className="flex items-center gap-4">
          <Globe className={settingsVariant ? "h-5 w-5 text-[#5b616d]" : "w-6 h-6 text-[#9A9A9A]"} />
          <span className={settingsVariant ? "text-base font-medium leading-6 text-[#0a0c11]" : "text-black text-sm font-normal font-inter"}>
            {t("settings.language.title")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className={settingsVariant ? "text-sm leading-[21px] text-[#5b616d]" : "text-[#9A9A9A] text-sm font-normal font-inter"}>
            {t(LANGUAGE_LABEL_KEY[current])}
          </span>
          <ChevronRight className={settingsVariant ? "h-5 w-5 text-[#5b616d]" : "w-6 h-6 text-black"} />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[200px]">
        <DropdownMenuLabel>{t("settings.language.title")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang}
            onSelect={() => void handleSelect(lang)}
            aria-current={lang === current ? "true" : undefined}
            className="flex items-center justify-between gap-3"
          >
            <span>{t(LANGUAGE_LABEL_KEY[lang])}</span>
            {lang === current && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

export default LanguageSelector;
